import { Ikon } from '@studio/design-system/button';
import { Provider as PortalProvider } from '@studio/design-system/portal';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { Children, useCallback, useEffect, useRef } from 'react';

/** Total number of segments in the progress bar. */
const PROGRESS_TOTAL_STEPS = 5;

// Per ENG-3796 (Didier ITB #1): a small X glyph for the persistent close
// affordance. Inline (matching the other one-off SVGs in the wizard) so the
// shell does not depend on a named icon-font glyph being present.
const CloseGlyph = (): ReactElement => (
  <svg aria-hidden='true' className='h-16 w-16' fill='none' viewBox='0 0 24 24'>
    <path d='M6 6l12 12M18 6L6 18' stroke='currentColor' strokeLinecap='round' strokeWidth='2' />
  </svg>
);

interface FunnelShellProps {
  readonly children: ReactNode;
  /** Screen title announced to screen readers on transition. */
  readonly announceTitle?: string | undefined;
  /** Called when Escape is pressed. Omit to prevent Escape dismissal. */
  readonly onEscape?: (() => void) | undefined;
  /** Show a subtle ambient glow behind content. */
  readonly glow?: boolean | undefined;
  /**
   * Current progress step (1-5). When greater than 0, a 5-segment
   * progress bar renders at the top of the dialog. Pass 0 or omit
   * to hide the bar (e.g. the dismissed screen has no progress).
   */
  readonly progressStep?: number | undefined;
  /**
   * The screen scrolls its OWN middle section, so the card must not scroll.
   *
   * Default (false) lets the whole card scroll, which is right for the short
   * screens. The discovery list is not short: with 30 cameras the footer's
   * Continue button sat below the fold and the title scrolled away with it, so
   * that screen keeps its header and footer fixed and scrolls the list alone
   * (Aleksey, 2026-08-25). The screen must then pass ONE child laid out as a
   * flex column.
   */
  readonly scrollable?: boolean | undefined;
}

// Shared full-bleed overlay for every screen in the bridge-connection
// funnel (RFD 0006) and the camera-add wizard. Uses native <dialog> for
// focus trapping, Escape handling, and inert-on-background — per the
// adversarial accessibility audit (C1, H1).
const STAGGER_BASE_MS = 80;

export const FunnelShell = ({
  children,
  announceTitle,
  onEscape,
  glow = false,
  progressStep = 0,
  scrollable = false
}: FunnelShellProps): ReactElement => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null || dialog.open) return;
    dialog.showModal();
  }, []);

  // Native <dialog> closes on Escape. Prevent default and route
  // through onEscape so screens without a dismiss action stay open.
  const handleCancel = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      onEscape?.();
    },
    [onEscape]
  );

  return (
    <dialog
      ref={dialogRef}
      aria-label='Onboarding'
      // The card is centred on both axes: items-center here, auto block
      // margins on the card, symmetric 5vh padding. It used to sit high (pt
      // 9vh / pb 5vh, justify-start) but on short windows that read as
      // misaligned. Every screen in the funnel shares this shell, so the
      // placement moves all of them together. Not justify-center: per rule
      // keyboard-reachable-interactive-elements (ai.knowledge/global), that
      // clips the top of an overflowing child in a scroll container, while
      // auto margins collapse to 0 and keep it reachable.
      //
      // m-0 / max-w-none / max-h-none / border-0 / px-16 undo the UA stylesheet
      // for a modal dialog (`margin: auto; padding: 1em; border: solid;
      // max-width/max-height: calc(100% - 6px - 2em)`). Without them the
      // "full-bleed" overlay is clamped to the viewport minus ~2em and centred,
      // leaving an undimmed, unblurred ring of studio chrome around the edge.
      // px-16 (not px-0) keeps equal 16px gutters either side of the card on a
      // window narrower than its 42rem max width; the Electron window has no
      // minimum size, so that case is reachable.
      //
      // w-auto / h-auto (not w-screen / h-screen): the UA gives <dialog>
      // `width/height: fit-content`, so an explicit auto is needed for inset-0
      // to stretch it. 100vw/100vh would also work but they include a classic
      // root scrollbar, so if the studio behind ever overflows the dialog ends
      // up scrollbar-width wider than the visible viewport and the card lands
      // ~8px right of centre. inset-0 sizes to the layout viewport instead.
      className='fixed inset-0 z-100 m-0 flex h-auto max-h-none w-auto max-w-none flex-col items-center overflow-y-auto border-0 bg-transparent px-16 py-[5vh] backdrop:bg-transparent'
      onCancel={handleCancel}
    >
      {/* Onboarding-specific keyframes — only exist while the dialog is mounted */}
      <style>{`
        @keyframes scale-up { from { transform: scale(0.6); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes draw-check { to { stroke-dashoffset: 0; } }
      `}</style>
      {/* Semi-transparent backdrop — the Studio dashboard stays visible
          behind the wizard, dimmed and blurred per the PM prototype. Fixed,
          not absolute, so it is pinned to the viewport regardless of the
          dialog's scroll position. The 90vh card cap below means the dialog
          should never actually scroll; `overflow-y-auto` on it and this fixed
          backdrop are the safety net if a future screen breaks that cap. */}
      <div aria-hidden='true' className='fixed inset-0 bg-dark/25 backdrop-blur-sm' />

      {/* Wizard card — bordered, rounded container floating above the backdrop.
          rounded-12 is the largest radius token in the design system (the
          config.cjs borderRadius scale tops out at 12); rounded-16 is not a
          defined token and silently renders square corners. */}
      {/* 90vh + the shell's 5vh/5vh padding is exactly the viewport, so the
          card fits a short window without the dialog itself scrolling; my-auto
          centres it vertically and degrades safely if it ever does overflow. */}
      <div
        className={`shadow-2xl relative z-10 my-auto flex max-h-[90vh] w-full max-w-[42rem] flex-col rounded-12 border border-base-100 bg-base-300 ${
          scrollable ? 'h-[90vh] overflow-hidden' : 'overflow-y-auto'
        }`}
      >
        {/* Persistent close (X) affordance — Didier ITB #1: every step must
            offer a visible, keyboard-focusable way out (the detecting screen
            was previously a hard trap with no exit at all). Rendered only when
            the screen supplies an onEscape handler; routing through onEscape
            keeps a single dismissal path shared with the Escape key. Ikon is a
            real design-system <button> (focusable, type=button) per
            ui-architecture — never a bare <div onClick>.

            It is given flex/items-center/justify-center because the glyph above
            is a raw <svg>, not the icon-font character Ikon is normally handed.
            Ikon's root is a plain <button>, so an inline SVG sits on the text
            baseline and hugs the inline start edge: inside a 24px button a 16px
            glyph lands 8px left of centre and a couple of pixels high, which is
            the off-centre X Aleksey caught on 2026-08-18. Centring here rather
            than inside the shared Ikon leaves every other button in the app
            pixel-identical. */}
        {onEscape !== undefined && (
          <Ikon
            aria-label='Skip and close'
            className='absolute right-12 top-12 z-20 flex items-center justify-center text-secondary hover:text-foreground'
            icon={<CloseGlyph />}
            onClick={onEscape}
          />
        )}
        {/* 5-segment progress bar at the top of the card */}
        {progressStep > 0 && (
          <div
            aria-valuenow={progressStep}
            aria-valuemin={1}
            aria-valuemax={PROGRESS_TOTAL_STEPS}
            className='flex h-4 w-full overflow-hidden bg-base-200'
            role='progressbar'
          >
            {Array.from({ length: PROGRESS_TOTAL_STEPS }, (_, i) => (
              <div
                key={i}
                className={
                  'duration-300 h-full flex-1 transition-colors ' +
                  (i < progressStep ? 'bg-primary' : 'bg-transparent')
                }
              />
            ))}
          </div>
        )}
        {glow && (
          <div
            aria-hidden='true'
            className='pointer-events-none absolute inset-0'
            style={{
              background:
                'radial-gradient(ellipse 50% 40% at 50% 45%, var(--ds-primary) 0%, transparent 100%)',
              opacity: 0.06
            }}
          />
        )}
        {announceTitle !== undefined && (
          <div aria-atomic='true' aria-live='polite' className='sr-only'>
            {announceTitle}
          </div>
        )}
        {/* Portal container for popper content (Select dropdowns, tooltips).
            The design system's <Portal> defaults to document.body, and this
            shell is a native <dialog> opened with showModal() — which puts it in
            the browser's TOP LAYER. Anything portaled to body therefore paints
            UNDERNEATH the dialog no matter its z-index, and Select.Content
            already carries z-[500]. Providing a container that lives inside the
            dialog is the fix, and it applies to every funnel screen rather than
            being patched per-dropdown. */}
        {/* No sizing class: Provider renders its container as `fixed z-50`, so
            giving it inset-0 turns it into a full-screen invisible overlay that
            swallows every click on the wizard. With no dimensions it occupies
            nothing, and Radix positions its own popper against the trigger. */}
        <PortalProvider>
          <div
            className={`relative flex w-full flex-col items-center text-center ${
              scrollable ? 'min-h-0 flex-1 overflow-hidden px-32 py-32' : 'px-32 py-48'
            }`}
          >
            {Children.map(children, (child, i) => {
              if (child === null || child === undefined || child === false) return null;
              const delay: CSSProperties = {
                opacity: 0,
                animation: `fade-in 375ms ease-out ${i * STAGGER_BASE_MS}ms forwards`
              };
              return (
                <div
                  className={`flex w-full flex-col items-center ${
                    scrollable ? 'min-h-0 flex-1' : ''
                  }`}
                  style={delay}
                >
                  {child}
                </div>
              );
            })}
          </div>
        </PortalProvider>
      </div>
    </dialog>
  );
};
